import { useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  SnackbarCloseReason,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { useNavigate } from 'react-router-dom';

dayjs.locale('pt-br');

import { LayoutBaseDePagina } from '../../shared/layouts';
import { DespesaService, IDespesaFormData, IDespesaListagemItem } from '../../shared/services/api/controle/DespesaService';
import { MoneyTextField } from '../../shared/forms';
import { proximoDiaUtil } from '../../shared/utils/feriadosBrasil';
import { brand } from '../../shared/themes';

const formatarMoeda = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const CATEGORIAS = ['Infraestrutura', 'Marketing', 'Equipe', 'Impostos', 'Serviços', 'Outros'];

const MESES_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const vazio: IDespesaFormData = { descricao: '', categoria: '', valor: 0, data: dayjs().format('YYYY-MM-DD'), observacao: '' };

export const ListagemDeDespesas: React.FC = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState<IDespesaListagemItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const [openDialog, setOpenDialog] = useState(false);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [form, setForm] = useState<IDespesaFormData>(vazio);
  const [isSalvando, setIsSalvando] = useState(false);

  const [openGerar, setOpenGerar] = useState(false);
  const [descricaoGerar, setDescricaoGerar] = useState('');
  const [categoriaGerar, setCategoriaGerar] = useState('');
  const [valorGerar, setValorGerar] = useState(0);
  const [observacaoGerar, setObservacaoGerar] = useState('');
  const [anoInicialGerar, setAnoInicialGerar] = useState(String(new Date().getFullYear()));
  const [mesInicialGerar, setMesInicialGerar] = useState(String(new Date().getMonth() + 1));
  const [quantidadeMesesGerar, setQuantidadeMesesGerar] = useState('12');
  const [diaLancamentoGerar, setDiaLancamentoGerar] = useState('5');
  const [isGerando, setIsGerando] = useState(false);

  const totalListado = useMemo(() => rows.reduce((soma, d) => soma + Number(d.valortotal), 0), [rows]);

  const handleCloseMsg = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
  };

  const carregar = () => {
    setIsLoading(true);
    DespesaService.getAll(1, filter, 100).then((result) => {
      setIsLoading(false);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      setRows(result.data);
      setTotalCount(result.totalCount);
    });
  };

  useEffect(() => { carregar(); }, [filter]);

  const handleNovo = () => {
    setEditandoId(null);
    setForm(vazio);
    setOpenDialog(true);
  };

  const handleEditar = (despesa: IDespesaListagemItem) => {
    if (!despesa.iddespesa) return;
    setEditandoId(despesa.iddespesa);
    setForm({
      descricao: despesa.descricao,
      categoria: despesa.categoria || '',
      valor: despesa.valor,
      data: dayjs(despesa.data).format('YYYY-MM-DD'),
      observacao: despesa.observacao || '',
    });
    setOpenDialog(true);
  };

  const handleSalvar = () => {
    if (!form.descricao || !form.valor) {
      setTms('error');
      setMsg('Preencha a descrição e o valor.');
      setOpenmsg(true);
      return;
    }
    setIsSalvando(true);
    const acao = editandoId ? DespesaService.updateById(editandoId, form) : DespesaService.create(form);
    acao.then((result) => {
      setIsSalvando(false);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      setOpenDialog(false);
      setTms('success');
      setMsg('Despesa salva com sucesso!');
      setOpenmsg(true);
      carregar();
    });
  };

  const handleApagar = (despesa: IDespesaListagemItem) => {
    if (!despesa.iddespesa) return;
    if (!window.confirm(`Apagar a despesa "${despesa.descricao}"?`)) return;
    DespesaService.deleteById(despesa.iddespesa).then((result) => {
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      carregar();
    });
  };

  const handleAbrirGerar = () => {
    setDescricaoGerar('');
    setCategoriaGerar('');
    setValorGerar(0);
    setObservacaoGerar('');
    setAnoInicialGerar(String(new Date().getFullYear()));
    setMesInicialGerar(String(new Date().getMonth() + 1));
    setQuantidadeMesesGerar('12');
    setDiaLancamentoGerar('5');
    setOpenGerar(true);
  };

  // Prévia dos lançamentos que serão gerados — mesma regra de rolagem de mês/ano e de
  // ajuste pro próximo dia útil (fim de semana/feriado nacional) da geração de
  // mensalidades (ver DetalheEmpresa.tsx / GerarAno.ts no backend).
  const parcelasPreview = useMemo(() => {
    const ano = Number(anoInicialGerar);
    const mesInicial = Number(mesInicialGerar);
    const quantidade = Number(quantidadeMesesGerar);
    const diaLancamento = Number(diaLancamentoGerar);
    if (!ano || !mesInicial || !quantidade || quantidade <= 0 || !diaLancamento) return [];

    return Array.from({ length: Math.min(quantidade, 60) }, (_, i) => {
      const indiceMes = mesInicial - 1 + i;
      const anoParcela = ano + Math.floor(indiceMes / 12);
      const mesParcela = (indiceMes % 12) + 1;
      const ultimoDia = new Date(anoParcela, mesParcela, 0).getDate();
      const dia = Math.min(diaLancamento, ultimoDia);
      const data = proximoDiaUtil(new Date(anoParcela, mesParcela - 1, dia));
      return { data: dayjs(data) };
    });
  }, [anoInicialGerar, mesInicialGerar, quantidadeMesesGerar, diaLancamentoGerar]);

  const totalPreview = parcelasPreview.length * valorGerar;

  const handleGerarRecorrente = () => {
    if (!descricaoGerar) {
      setTms('error');
      setMsg('Informe a descrição da despesa.');
      setOpenmsg(true);
      return;
    }
    if (!valorGerar || valorGerar <= 0) {
      setTms('error');
      setMsg('Informe um valor válido.');
      setOpenmsg(true);
      return;
    }
    setIsGerando(true);
    DespesaService.gerarRecorrente({
      descricao: descricaoGerar,
      categoria: categoriaGerar || null,
      valor: valorGerar,
      observacao: observacaoGerar || null,
      anoinicial: Number(anoInicialGerar),
      mesinicial: Number(mesInicialGerar),
      quantidademeses: Number(quantidadeMesesGerar),
      dialancamento: Number(diaLancamentoGerar),
    }).then((result) => {
      setIsGerando(false);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      setOpenGerar(false);
      setTms('success');
      setMsg(`${quantidadeMesesGerar} despesa${Number(quantidadeMesesGerar) !== 1 ? 's' : ''} gerada${Number(quantidadeMesesGerar) !== 1 ? 's' : ''} com sucesso!`);
      setOpenmsg(true);
      carregar();
    });
  };

  return (
    <LayoutBaseDePagina
      titulo='Despesas'
      subtitulo='Lançamentos de despesas usados no cálculo do saldo do painel.'
      acao={
        <Box display='flex' gap={1}>
          <Button
            variant='outlined'
            disableElevation
            startIcon={<EventRepeatRoundedIcon />}
            onClick={handleAbrirGerar}
            sx={{ height: 44, borderRadius: '10px', px: 2.5, fontWeight: 600 }}
          >
            Gerar recorrente
          </Button>
          <Button
            variant='contained'
            disableElevation
            startIcon={<AddCircleOutlineRoundedIcon />}
            onClick={handleNovo}
            sx={{ height: 44, borderRadius: '10px', px: 2.5, fontWeight: 600, bgcolor: brand.menuBase, color: '#fff', '&:hover': { bgcolor: '#012D3D' } }}
          >
            Nova Despesa
          </Button>
        </Box>
      }
      barraDeFerramentas={
        <Box sx={{ mx: 1, mb: 2.5 }}>
          <TextField
            size='small'
            placeholder='Pesquisar por descrição...'
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ minWidth: 280 }}
          />
        </Box>
      }
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openmsg}
        autoHideDuration={tms === 'error' ? null : 6000}
        onClose={handleCloseMsg}
      >
        <Alert onClose={handleCloseMsg} severity={tms} variant='filled' sx={{ width: '100%' }}>
          {msg}
        </Alert>
      </Snackbar>

      <Paper elevation={0} sx={{ borderRadius: '12px' }}>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell align='center'>Parcelas</TableCell>
                <TableCell align='right'>Valor</TableCell>
                <TableCell align='right' sx={{ width: 96 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((despesa) => {
                const ehRecorrencia = !!despesa.idrecorrencia;
                return (
                  <TableRow
                    key={despesa.chave}
                    hover={ehRecorrencia}
                    onClick={ehRecorrencia ? () => navigate(`/despesas/${despesa.idrecorrencia}`) : undefined}
                    sx={ehRecorrencia ? { cursor: 'pointer' } : undefined}
                  >
                    <TableCell>{dayjs(despesa.data).format('DD/MM/YYYY')}{ehRecorrencia && ' (última)'}</TableCell>
                    <TableCell>
                      {despesa.descricao}
                      {ehRecorrencia && <EventRepeatRoundedIcon fontSize='inherit' sx={{ ml: 0.5, verticalAlign: 'middle', color: 'text.secondary' }} />}
                    </TableCell>
                    <TableCell>{despesa.categoria || '-'}</TableCell>
                    <TableCell align='center'>
                      {ehRecorrencia ? (
                        <Chip
                          label={`${despesa.totalpagas}/${despesa.quantidadeparcelas} pagas`}
                          size='small'
                          color={despesa.totalpagas === despesa.quantidadeparcelas ? 'success' : 'warning'}
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell align='right'>{formatarMoeda(despesa.valortotal)}</TableCell>
                    <TableCell align='right' onClick={(e) => e.stopPropagation()}>
                      {ehRecorrencia ? (
                        <IconButton size='small' onClick={() => navigate(`/despesas/${despesa.idrecorrencia}`)}>
                          <ChevronRightRoundedIcon fontSize='small' />
                        </IconButton>
                      ) : (
                        <>
                          <IconButton size='small' onClick={() => handleEditar(despesa)}>
                            <EditRoundedIcon fontSize='small' />
                          </IconButton>
                          <IconButton size='small' onClick={() => handleApagar(despesa)}>
                            <DeleteOutlineRoundedIcon fontSize='small' />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align='center'>
                    <Typography color='text.secondary' fontSize='0.85rem' sx={{ py: 3 }}>
                      Nenhuma despesa lançada ainda.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={4} sx={{ fontWeight: 700 }}>Total ({totalCount})</TableCell>
                <TableCell align='right' sx={{ fontWeight: 700 }}>{formatarMoeda(totalListado)}</TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth='xs' fullWidth>
        <DialogTitle>{editandoId ? 'Editar despesa' : 'Nova despesa'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label='Descrição' value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth select label='Categoria' value={form.categoria || ''} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
                {CATEGORIAS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type='date'
                label='Data'
                InputLabelProps={{ shrink: true }}
                value={form.data}
                onChange={(e) => setForm({ ...form, data: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Valor'
                type='number'
                value={form.valor}
                onChange={(e) => setForm({ ...form, valor: Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label='Observação'
                value={form.observacao || ''}
                onChange={(e) => setForm({ ...form, observacao: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant='contained' disabled={isSalvando} onClick={handleSalvar}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openGerar} onClose={() => setOpenGerar(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Gerar despesa recorrente</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label='Descrição' value={descricaoGerar} onChange={(e) => setDescricaoGerar(e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth select label='Categoria' value={categoriaGerar} onChange={(e) => setCategoriaGerar(e.target.value)}>
                <MenuItem value=''>Nenhuma</MenuItem>
                {CATEGORIAS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <MoneyTextField fullWidth label='Valor mensal' value={valorGerar} onChange={setValorGerar} />
            </Grid>

            <Grid item xs={6}>
              <TextField fullWidth label='Ano inicial' value={anoInicialGerar} onChange={(e) => setAnoInicialGerar(e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label='Dia do lançamento' value={diaLancamentoGerar} onChange={(e) => setDiaLancamentoGerar(e.target.value)} />
            </Grid>
            <Grid item xs={7}>
              <TextField
                fullWidth
                select
                label='Mês inicial'
                value={mesInicialGerar}
                onChange={(e) => setMesInicialGerar(e.target.value)}
              >
                {MESES_PT.map((mes, index) => (
                  <MenuItem key={mes} value={String(index + 1)}>{mes}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={5}>
              <TextField
                fullWidth
                type='number'
                label='Quantidade de meses'
                value={quantidadeMesesGerar}
                onChange={(e) => setQuantidadeMesesGerar(e.target.value)}
                inputProps={{ min: 1, max: 60 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label='Observação'
                value={observacaoGerar}
                onChange={(e) => setObservacaoGerar(e.target.value)}
              />
            </Grid>

            {parcelasPreview.length > 0 && (
              <Grid item xs={12}>
                <Typography variant='subtitle2' fontWeight={700} gutterBottom>
                  Prévia dos lançamentos
                </Typography>
                <TableContainer sx={{ maxHeight: 220, border: '1px solid rgba(0,0,0,0.08)', borderRadius: '8px' }}>
                  <Table size='small' stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Data</TableCell>
                        <TableCell align='right'>Valor</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parcelasPreview.map((p, index) => (
                        <TableRow key={index}>
                          <TableCell>{p.data.format('DD/MM/YYYY')}</TableCell>
                          <TableCell align='right'>{formatarMoeda(valorGerar)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box display='flex' justifyContent='space-between' sx={{ mt: 1, px: 0.5 }}>
                  <Typography fontWeight={700}>Total ({parcelasPreview.length}x)</Typography>
                  <Typography fontWeight={700}>{formatarMoeda(totalPreview)}</Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenGerar(false)}>Cancelar</Button>
          <Button variant='contained' disabled={isGerando} onClick={handleGerarRecorrente}>
            Gerar {quantidadeMesesGerar || ''} {Number(quantidadeMesesGerar) === 1 ? 'lançamento' : 'lançamentos'}
          </Button>
        </DialogActions>
      </Dialog>
    </LayoutBaseDePagina>
  );
};
